import { Cart, OperationType } from "Store/cart/cartInterfaces";
import {
  fetchCartStart,
  operationFailed,
  removeProductsStart,
  removeProductsSucceeded,
  updateProductInStoreOnlyStart,
  updateProductInStoreOnlySucceeded,
  updateProductStart,
  updateProductSucceeded,
} from "Store/cart/cartSlice";
import { nextStep, updateActiveStep } from "Store/steps/stepsSlice";
import {
  ecommerceEventProductsFrom,
  pushEcommerceEvent,
} from "Utils/analytics";
import { CART_RESOURCE_ROOT, getResourceRoot } from "Utils/apiUtils";
import {
  getCampaignAndLeadQueryParams,
  getUpdateAdviceQueryParam,
} from "Utils/navigation/queryParameterUtils";
import { createRequest, getError } from "Utils/requestUtils";
import { call, put, select, takeLatest } from "redux-saga/effects";

import { analyticsRemoveFromCart } from "@storebrand-digital/insurance-analytics";
import { getLastCheckoutProductStepId } from "Components/Features/CheckOut/checkoutUtils";
import { Uri } from "Constants/constants";
import { isAdviceProductOnCart } from "Store/advice/utils";
import { AnalyticsState } from "Store/analytics/analyticsInterfaces";
import { fetchCart } from "Store/cart/cartSagas";
import { definedCartSelector } from "Store/cart/cartSelectors";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { PartnerState } from "Store/partners/partnerInterfaces";
import { ReduxState } from "Store/rootReducer";
import { isAuthenticated } from "Utils/KeycloakWrapper";
import { formatDateToString } from "Utils/dateUtils";
import { recordProductIfRecording } from "Utils/fixtureRecordingUtils";
import { isDefined } from "Utils/functions";
import { getProductSubtitle } from "./productUtils";

const partnerSelector = (state: ReduxState): PartnerState => state.partner;
const analyticsSelector = (state: ReduxState): AnalyticsState =>
  state.analytics;

export function* updateProduct({
  payload,
}: ReturnType<typeof updateProductStart>): any {
  try {
    const insuranceProduct = payload as InsuranceProduct;
    const cart: Cart = yield select(definedCartSelector);
    const cartIdFromState = cart.cartId;
    const lastProductId = getLastCheckoutProductStepId(cart);
    const url = `${CART_RESOURCE_ROOT}/${Uri.PRODUCTS}/${insuranceProduct.productId}`;
    const { issueDate, ...insuranceProductPayload } = insuranceProduct;
    const { data } = yield call(createRequest, {
      method: "patch",
      requestBody: {
        issueDate: issueDate ? formatDateToString(issueDate) : "",
        ...insuranceProductPayload,
      },
      url,
      params: getCampaignAndLeadQueryParams(),
      open: false,
    });
    recordProductIfRecording(data);
    const updatedSubTitle = getProductSubtitle(insuranceProduct);
    if (isDefined(updatedSubTitle)) {
      yield put(updateActiveStep({ subTitle: updatedSubTitle }));
    }
    if (insuranceProduct.productId === lastProductId) {
      yield call(fetchCart, cartIdFromState, true);
    } else {
      yield put(updateProductSucceeded(data));
    }
    yield put(nextStep());
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.UPDATE_PRODUCT,
        response: getError(e),
      }),
    );
  }
}

export function* updateProductInStoreOnly({
  payload,
}: ReturnType<typeof updateProductInStoreOnlyStart>): any {
  try {
    yield put(updateProductInStoreOnlySucceeded(payload));
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.UPDATE_PRODUCT_IN_STORE_ONLY,
        response: getError(e),
      }),
    );
  }
}

export function* removeProducts({
  payload,
}: ReturnType<typeof removeProductsStart>): any {
  try {
    const cart: Cart = yield select(definedCartSelector);
    const cartIdFromState = cart.cartId;

    for (const product of payload) {
      const deleteProductUrl = `${getResourceRoot()}/${cartIdFromState}/${
        Uri.PRODUCTS
      }/${product.productId}`;
      const adviceProduct = isAdviceProductOnCart(
        product as InsuranceProduct,
        cart,
      );
      yield call(createRequest, {
        method: "delete",
        requestBody: payload,
        url: deleteProductUrl,
        params: adviceProduct ? getUpdateAdviceQueryParam() : undefined,
        open: !isAuthenticated(),
      });
    }
    const partner: PartnerState = yield select(partnerSelector);
    const analytics: AnalyticsState = yield select(analyticsSelector);

    pushEcommerceEvent(
      analyticsRemoveFromCart({
        cartId: cart.cartId,
        products: ecommerceEventProductsFrom(cart.products),
        removedProductIds: payload.map((p) => p.productId),
        loginMethod: analytics.loginMethod,
        partnerName: partner.partnerId,
        features: analytics.usedFeatureFlags,
      }),
    );

    yield put(removeProductsSucceeded(payload));
    yield put(fetchCartStart({ cartId: cartIdFromState }));
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.REMOVE_PRODUCT,
        response: getError(e),
      }),
    );
  }
}

export function* productSaga(): Generator {
  yield takeLatest(removeProductsStart, removeProducts);
  yield takeLatest(updateProductStart, updateProduct);
  yield takeLatest(updateProductInStoreOnlyStart, updateProductInStoreOnly);
}
